import {createAsyncThunk, createSlice, PayloadAction} from '@reduxjs/toolkit'
import {http} from 'hiya-common/src/utils/axios'
import {HostMaps} from 'hiya-common/src/utils/host/AutoHost'
import 公会 from './images/公会.png'
import 主播 from './images/主播.png'
import 人物 from './images/人物.png'
import 游戏 from './images/游戏.png'
import 许愿池 from './images/许愿池.png'

// 倒计时配置常量
export const COUNTDOWN_CONFIG = {
  text: { day: "D", hour: "H", minute: "M", second: "S" },
  style_config: {
    time: {
      bg: `url(${HostMaps.auto.otherStatic}/static/all/690c17648767c3a943fcf5db)`,
      color: "#FFDF74",
    },
    desc_color: "#FFE5A2",
    separator: false,
    separator_color: "#EBD397",
  },
} as const

export enum ActivityType {
  公会 = 1,
  人物,
  游戏,
  主播,
  许愿池,
}

// 赛程类型
export enum ScheduleType {
  入围赛 = 1,
  晋级赛 = 2,
  总决赛 = 3,
  荣耀殿堂 = 4,
}

// Tab 基础配置（图标、文案等）
export const TabConfigMap: { [key: number]: {icon: string; label: string; log_type: string; date: string} } = {
  [ActivityType.公会]: {icon: 公会, label: '公会榜', log_type: 'annual agency', date: '12.1-12.6'},
  [ActivityType.人物]: {icon: 人物, label: '土豪榜', log_type: 'annual tycoon', date: '12.1-12.12'},
  [ActivityType.游戏]: {icon: 游戏, label: '年度游戏', log_type: 'annual game', date: '12.13-12.18'},
  [ActivityType.主播]: {icon: 主播, label: '主播榜', log_type: 'annual anchor', date: '12.7-12.12'},
  [ActivityType.许愿池]: {icon: 许愿池, label: '许愿池', log_type: 'annual wishing well', date: '12.1-12.12'},
} as const

// 默认排序规则
const DefaultSortRule: { [key: number]: number } = {
  [ActivityType.公会]: 1,
  [ActivityType.人物]: 2,
  [ActivityType.游戏]: 3,
  [ActivityType.主播]: 4,
  [ActivityType.许愿池]: 5,
}

// Tab 排序规则：根据 curr_tab_type 决定每个 tab 的排序位置
export const TabSortRules: Partial<{ [key: number]: { [key2: number]: number } }> = {
  // 当 curr_tab_type = 1(公会) 时的排序规则
  [ActivityType.公会]: {
    [ActivityType.公会]: 1,   // 年度公会-1
    [ActivityType.主播]: 4,   // 年度主播-4
    [ActivityType.人物]: 2,   // 年度人物-2
    [ActivityType.许愿池]: 3, // 盛典许愿池-3
    [ActivityType.游戏]: 5,   // 年度游戏-5
  },
  // 当 curr_tab_type = 4(主播) 时的排序规则
  [ActivityType.主播]: {
    [ActivityType.公会]: 4,   // 年度公会-4
    [ActivityType.主播]: 1,   // 年度主播-1
    [ActivityType.人物]: 2,   // 年度人物-2
    [ActivityType.许愿池]: 3, // 盛典许愿池-3
    [ActivityType.游戏]: 5,   // 年度游戏-5
  },
  // 当 curr_tab_type = 5(游戏) 时的排序规则
  [ActivityType.游戏]: {
    [ActivityType.公会]: 4,   // 年度公会-4
    [ActivityType.主播]: 5,   // 年度主播-5
    [ActivityType.人物]: 2,   // 年度人物-2
    [ActivityType.许愿池]: 3, // 盛典许愿池-3
    [ActivityType.游戏]: 1,   // 年度游戏-1
  },
}

// 根据 curr_tab_type 生成排序后的 TabList
export const getSortedTabList = (currTabType: ActivityType) => {
  // 如果没有特定规则，使用默认排序
  const sortRule = TabSortRules[currTabType] || DefaultSortRule
  
  return Object.keys(TabConfigMap)
    .map((key) => {
      const type = Number(key) as ActivityType
      return {
        sort: sortRule[type],
        key: type,
        ...TabConfigMap[type],
      }
    })
    .sort((a, b) => a.sort - b.sort)
}

// 获取tab数据以及活动配置 http://wiki.xunlei.cn/pages/viewpage.action?pageId=174051536&flashId=1830565352
export const getTabData = createAsyncThunk(
  '/activity/api/annual/2025/v1/get_tab_data',
  async (arg, store: any) => {
    const resp = await http.post(
      '/activity/api/annual/2025/v1/get_tab_data',
      {
        ...store.getState().env.deviceinfo,
      },
      {
        xcErrorToast: true,
      }
    )
    // 从 store 中获取 otherInfo.type，包含在返回数据中
    const type = store.getState().env.otherInfo?.type
    return {
      ...resp.data.data,
      type, // 将 type 包含在返回数据中，供 extraReducers 使用
    }
  }
)

// 获取游戏配置 http://wiki.xunlei.cn/pages/viewpage.action?pageId=174051618
export const getGameData = createAsyncThunk(
  '/activity/api/h5/v1/divide_coin_get_base_data',
  async (arg, store: any) => {
    const resp = await http.post(
      '/activity/api/h5/v1/divide_coin_get_base_data',
      {
        ...store.getState().env.deviceinfo,
        activity_id: store.getState().AnnualActivity2025.activityId,
      },
      {
        xcErrorToast: true,
      }
    )
    return {
      ...resp.data.data,
    }
  }
)

// 获取游戏配置 http://wiki.xunlei.cn/pages/viewpage.action?pageId=174051618
export const getGameRecordList = createAsyncThunk(
  '/activity/api/h5/v1/divide_coin_get_receive_list',
  async (arg: any, store: any) => {
    const resp = await http.post(
      '/activity/api/h5/v1/divide_coin_get_receive_list',
      {
        ...store.getState().env.deviceinfo,
        ...arg,
      },
      {
        xcErrorToast: true,
      }
    )
    return {
      ...resp.data,
    }
  }
)

// 瓜分奖池 http://wiki.xunlei.cn/pages/viewpage.action?pageId=174051634
export const divideCoinReceive = createAsyncThunk(
  '/activity/api/h5/v1/divide_coin_receive',
  async (arg: any, store: any) => {
    const resp = await http.post(
      '/activity/api/h5/v1/divide_coin_receive',
      {
        ...store.getState().env.deviceinfo,
        ...arg,
      },
      {
        xcErrorToast: true,
      }
    )
    return {
      ...resp.data,
    }
  }
)


interface InitialStateType {
  activityId: number, // 活动id
  curSchedule: any, // 当前赛程
  curTab: ActivityType, // 当前tab
  modalObj: any, // h5弹窗提示信息
  tabList: Array<any> // tab列表 赛程展示顺序不同
  subTabData: { [key: number]: Array<any> } // 赛程list，key为tab类型，value为对应的列表数据
  isFame: boolean // 是否展示荣誉殿堂
  gameData: any // 游戏配置
}

const initialState: InitialStateType = {
  activityId: null, // 活动id
  curSchedule: {}, // 当前赛程
  curTab: ActivityType.公会, // 当前tab
  modalObj: {
    tips_title: '',
    tips_content: '',
  }, // h5弹窗提示信息
  tabList: getSortedTabList(ActivityType.公会), // tab列表
  subTabData: {} as { [key: number]: Array<any> }, // 赛程list，格式为 {1: [], 2: [], ...}
  isFame: false, // 是否展示荣誉殿堂
  gameData: {}, // 游戏配置
}

export const AnnualActivity2025Slice = createSlice({
  name: 'AnnualActivity2025',
  initialState,
  reducers: {
    // 设置当前活动tab
    setCurTab: (state, action: PayloadAction<ActivityType>) => {
      state.curTab = action.payload
    },
    // 设置当前赛程
    setCurSchedule: (state, action: PayloadAction<any>) => {
      state.curSchedule = action.payload
    },
    // 是否展示荣誉殿堂
    setIsFame: (state, action: PayloadAction<boolean>) => {
      state.isFame = action.payload
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getTabData.fulfilled, (state, action) => {
      // 从 action.payload 中获取 type（已在 createAsyncThunk 中从 store 获取并传递）
      const {curr_tab_type, sub_tab_data, activity_id, tips_title, tips_content, type} = action.payload
      Object.assign(state, {
        curTab: Number(type) || curr_tab_type,  
        activityId: activity_id,
        tabList: getSortedTabList(curr_tab_type),  // 根据 curr_tab_type 生成排序后的 TabList
        subTabData: sub_tab_data || {}, // 子tab数据，格式为 {1: [], 2: [], ...}
        curSchedule: sub_tab_data?.[curr_tab_type]?.find((item: any) => item?.is_select),
        isFame: sub_tab_data?.[curr_tab_type]?.findIndex((item: any) => item?.is_select) === ScheduleType.荣耀殿堂,
        modalObj: {
          tips_title: tips_title || '',
          tips_content: tips_content || '',
        },
      })
    })
    builder.addCase(getGameData.fulfilled, (state, action) => {
      state.gameData = action.payload
    })
  },
})
export default AnnualActivity2025Slice.reducer
export const AnnualActivity2025Actions = AnnualActivity2025Slice.actions
